import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Page } from '../components/Page.js';

type SnapshotMeta = { id: string; snapshot_date: string; item_count: number; imported_at_utc: string; raw_sha256: string; ledger_seq: number };
type Resolution = { resolution: string; notified_date: string | null; effective_date: string | null };
type Stale = { item_id: string; police: string; brand: string; type: string; resolution: Resolution | null };
type Orphan = { device_uuid: string; label: string; status: string; resolution: Resolution | null };
type Confirm = { item_id: string; device_uuid: string; police: string; label: string; score: number; evidence: string[] };
type Matched = { item_id: string | null; device_uuid: string | null; label: string; police: string; score: number; via: string };
type Fungible = { group: string; police: number; yours: number; matched: number; surplus_police: number; surplus_yours: number };
type Buckets = {
  matched: Matched[]; needs_confirm: Confirm[]; on_police_not_yours: Stale[]; on_yours_not_police: Orphan[]; fungible: Fungible[];
  counts: { matched: number; needs_confirm: number; stale_open: number; stale_resolved: number; yours_not_police: number };
};
type Device = { uuid: string; label: string };

const todayISO = () => new Date().toISOString().slice(0, 10);

export function Reconciliation() {
  const [snapshot, setSnapshot] = useState<SnapshotMeta | null>(null);
  const [buckets, setBuckets] = useState<Buckets | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [showMatched, setShowMatched] = useState(false);
  const [openRow, setOpenRow] = useState<string | null>(null);

  async function load() {
    const [rec, dev] = await Promise.all([
      fetch('/api/reconcile').then((r) => r.json()).catch(() => ({})),
      fetch('/api/devices').then((r) => r.json()).catch(() => ({ devices: [] })),
    ]);
    setSnapshot(rec.snapshot ?? null);
    setBuckets(rec.buckets ?? null);
    setDevices(dev.devices ?? []);
  }
  useEffect(() => { load(); }, []);

  return (
    <Page
      eyebrow="Compliance"
      title="Police-list reconciliation"
      actions={snapshot && (
        <span className="pill pill-ok">
          <span className={`dot dot-${buckets && buckets.counts.stale_open > 0 ? 'bad' : 'ok'}`} />
          {buckets ? `${buckets.counts.stale_open} stale on police list` : '…'}
        </span>
      )}
    >
      {!snapshot && <ImportPanel onDone={load} />}

      {snapshot && buckets && (
        <>
          <section className="card card-pad" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <strong>Police list as held on {snapshot.snapshot_date}</strong>
                <div className="muted" style={{ fontSize: 12.5 }}>
                  {snapshot.item_count} items · frozen + anchored at ledger seq {snapshot.ledger_seq} · SHA-256 {snapshot.raw_sha256.slice(0, 12)}…
                </div>
              </div>
              <details>
                <summary className="muted" style={{ fontSize: 12.5, cursor: 'pointer' }}>import a newer list</summary>
                <div style={{ marginTop: 10 }}><ImportPanel onDone={load} compact /></div>
              </details>
            </div>
          </section>

          <section className="stats" style={{ marginBottom: 16 }}>
            <div className="card card-pad stat"><div className="lab">Stale on police list</div><div className="num bad">{buckets.counts.stale_open}</div><div className="sub">they hold, you don’t — needs reporting</div></div>
            <div className="card card-pad stat"><div className="lab">Matched</div><div className="num ok">{buckets.counts.matched}</div><div className="sub">on both lists</div></div>
            <div className="card card-pad stat"><div className="lab">Needs confirmation</div><div className="num warn">{buckets.counts.needs_confirm}</div><div className="sub">probable matches</div></div>
            <div className="card card-pad stat"><div className="lab">On yours, not police</div><div className="num warn">{buckets.counts.yours_not_police}</div><div className="sub">may need notifying</div></div>
          </section>

          {/* STALE — the headline bucket */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="panel-head"><h3>On the police list, not in your possession</h3><span className="hint">the stale entries — resolve each with a dated report</span></div>
            <div className="ledger">
              {buckets.on_police_not_yours.length === 0 && <div style={{ padding: 16, color: 'var(--ink3)', fontSize: 13 }}>None — your list and the police list agree.</div>}
              {buckets.on_police_not_yours.map((s) => (
                <div key={s.item_id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <div className="ledger-row" style={{ borderBottom: 'none' }}>
                    <span className={`badge badge-${s.resolution ? 'ok' : 'bad'}`}>{s.resolution ? s.resolution.resolution.toUpperCase() : 'STALE'}</span>
                    <div className="ledger-main">
                      <div className="ledger-text"><strong style={{ textTransform: 'capitalize' }}>{s.police}</strong></div>
                      <div className="ledger-time">
                        {s.resolution
                          ? `Resolved: ${s.resolution.resolution}${s.resolution.notified_date ? ` · police notified ${s.resolution.notified_date}` : ' · notification not yet recorded'}`
                          : `Police still hold this. ${s.brand || s.type ? `(${[s.brand, s.type].filter(Boolean).join(' / ')})` : ''}`}
                      </div>
                    </div>
                    {!s.resolution && (
                      <button className="btn btn-ghost" type="button" style={{ alignSelf: 'center' }} onClick={() => setOpenRow(openRow === s.item_id ? null : s.item_id)}>
                        Resolve
                      </button>
                    )}
                  </div>
                  {openRow === s.item_id && <ResolveForm item={s} devices={devices} onDone={() => { setOpenRow(null); load(); }} />}
                </div>
              ))}
            </div>
            <div className="panel-foot">Each resolution is anchored; reporting a disposal/sale/loss with a date creates the legal notification too.</div>
          </div>

          {/* NEEDS CONFIRM */}
          {buckets.needs_confirm.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="panel-head"><h3>Probable matches — please confirm</h3><span className="hint">{buckets.needs_confirm.length}</span></div>
              <div className="ledger">
                {buckets.needs_confirm.map((c) => (
                  <div className="ledger-row" key={c.item_id}>
                    <span className="badge badge-warn">{Math.round(c.score * 100)}%</span>
                    <div className="ledger-main">
                      <div className="ledger-text"><strong style={{ textTransform: 'capitalize' }}>{c.police}</strong> <span className="muted">↔ {c.label}</span></div>
                      <div className="ledger-time">{c.evidence.join(' · ') || 'fuzzy match'}</div>
                    </div>
                    <button className="btn btn-primary" type="button" style={{ alignSelf: 'center' }}
                      onClick={() => confirmMatch(c.item_id, c.device_uuid, load)}>Confirm</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ON YOURS NOT POLICE */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="panel-head"><h3>In your possession, not on the police list</h3><span className="hint">{buckets.on_yours_not_police.length}</span></div>
            <div className="ledger">
              {buckets.on_yours_not_police.length === 0 && <div style={{ padding: 16, color: 'var(--ink3)', fontSize: 13 }}>None.</div>}
              {buckets.on_yours_not_police.map((o) => (
                <div className="ledger-row" key={o.device_uuid}>
                  <span className="badge badge-warn">{o.status.toUpperCase()}</span>
                  <div className="ledger-main">
                    <div className="ledger-text"><strong>{o.label}</strong></div>
                    <div className="ledger-time">Not on the police list — may be a naming mismatch, or may need notifying.</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="panel-foot muted">Tip: if one of these is the same as a stale police entry under a different name, resolve that entry as “this is actually my device”.</div>
          </div>

          {/* FUNGIBLE + MATCHED */}
          {buckets.fungible.length > 0 && (
            <div className="card card-pad" style={{ marginBottom: 16 }}>
              <h3 style={{ marginTop: 0, fontSize: 14 }}>Counted by group (indistinguishable without serials)</h3>
              {buckets.fungible.map((f) => (
                <div key={f.group} className="muted" style={{ fontSize: 13 }}>
                  {f.group.replace('|', ' ')}: police {f.police} · yours {f.yours} · matched {f.matched}
                  {f.surplus_police > 0 ? ` · ${f.surplus_police} extra on police` : ''}{f.surplus_yours > 0 ? ` · ${f.surplus_yours} extra yours` : ''}
                </div>
              ))}
            </div>
          )}

          <button className="btn btn-ghost" type="button" onClick={() => setShowMatched((s) => !s)}>
            {showMatched ? 'Hide' : 'Show'} {buckets.matched.length} matched
          </button>
          {showMatched && (
            <div className="card" style={{ marginTop: 12 }}>
              <div className="ledger">
                {buckets.matched.map((m, i) => (
                  <div className="ledger-row" key={i}>
                    <span className="badge badge-ok">{m.via.toUpperCase()}</span>
                    <div className="ledger-main">
                      <div className="ledger-text"><strong style={{ textTransform: 'capitalize' }}>{m.police}</strong> <span className="muted">↔ {m.label}</span></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </Page>
  );
}

async function confirmMatch(itemId: string, deviceId: string, reload: () => void) {
  await fetch('/api/reconcile/aliases', {
    method: 'POST', headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ snapshot_item_id: itemId, device_id: deviceId, match_kind: 'same_device' }),
  });
  reload();
}

function ResolveForm({ item, devices, onDone }: { item: Stale; devices: Device[]; onDone: () => void }) {
  const [mode, setMode] = useState<'disposed' | 'mine' | 'never'>('disposed');
  const [resolution, setResolution] = useState('disposed');
  const [effective, setEffective] = useState(todayISO());
  const [notified, setNotified] = useState(todayISO());
  const [deviceId, setDeviceId] = useState(devices[0]?.uuid ?? '');
  const [busy, setBusy] = useState(false);
  const sorted = useMemo(() => [...devices].sort((a, b) => a.label.localeCompare(b.label)), [devices]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === 'mine') {
        await fetch('/api/reconcile/aliases', {
          method: 'POST', headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ snapshot_item_id: item.item_id, device_id: deviceId, match_kind: 'same_device' }),
        });
      } else if (mode === 'never') {
        await fetch('/api/reconcile/aliases', {
          method: 'POST', headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ snapshot_item_id: item.item_id, device_id: null, match_kind: 'never_owned' }),
        });
      } else {
        await fetch('/api/reconcile/resolve', {
          method: 'POST', headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ snapshot_item_id: item.item_id, bucket: 'on_police_not_yours', resolution, effective_date: effective, notified_date: notified, note: item.police }),
        });
      }
      onDone();
    } finally { setBusy(false); }
  }

  return (
    <form onSubmit={submit} style={{ padding: 12, background: 'var(--surface2)', borderTop: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        <button type="button" className={`btn ${mode === 'disposed' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setMode('disposed')}>It left my possession</button>
        <button type="button" className={`btn ${mode === 'mine' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setMode('mine')}>This is actually my device</button>
        <button type="button" className={`btn ${mode === 'never' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setMode('never')}>I never owned this</button>
      </div>

      {mode === 'disposed' && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <label className="fld" style={{ flex: '1 1 130px' }}><span>What happened</span>
            <select value={resolution} onChange={(e) => setResolution(e.target.value)}>
              <option value="disposed">Disposed</option><option value="sold">Sold</option>
              <option value="lost">Lost</option><option value="returned">Returned</option>
            </select>
          </label>
          <label className="fld" style={{ flex: '1 1 130px' }}><span>Date it happened</span>
            <input type="date" value={effective} max={todayISO()} onChange={(e) => setEffective(e.target.value)} />
          </label>
          <label className="fld" style={{ flex: '1 1 130px' }}><span>Date you told police</span>
            <input type="date" value={notified} max={todayISO()} onChange={(e) => setNotified(e.target.value)} />
          </label>
        </div>
      )}
      {mode === 'mine' && (
        <label className="fld"><span>Which of your devices is this?</span>
          <select value={deviceId} onChange={(e) => setDeviceId(e.target.value)}>
            {sorted.map((d) => <option key={d.uuid} value={d.uuid}>{d.label}</option>)}
          </select>
        </label>
      )}
      {mode === 'never' && <p className="muted" style={{ fontSize: 12.5 }}>Records that this entry was never yours, so it won’t flag again. Anchored to the ledger.</p>}

      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button className="btn btn-primary" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save (anchors to ledger)'}</button>
        <button className="btn btn-ghost" type="button" onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}

function ImportPanel({ onDone, compact }: { onDone: () => void; compact?: boolean }) {
  const [date, setDate] = useState(todayISO());
  const [paste, setPaste] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function importDocx(file: File) {
    setBusy(true); setErr(null);
    try {
      const b64 = await new Promise<string>((res, rej) => {
        const fr = new FileReader();
        fr.onload = () => res((fr.result as string).split(',')[1] ?? '');
        fr.onerror = rej; fr.readAsDataURL(file);
      });
      const r = await fetch('/api/police-snapshots', {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ snapshot_date: date, source_kind: 'docx', docx_base64: b64, source_filename: file.name }),
      });
      if (!r.ok) { setErr((await r.json()).error ?? 'Import failed'); return; }
      onDone();
    } catch { setErr('Could not read the file'); } finally { setBusy(false); }
  }

  async function importPaste(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const rows = paste.split('\n').map((l) => l.trim()).filter(Boolean).map((l) => {
        const parts = l.split(/\t|,|\s{2,}|\s\/\s/);
        return { col1: (parts[0] ?? '').trim(), col2: (parts[1] ?? '').trim() };
      });
      if (!rows.length) { setErr('Paste at least one line'); return; }
      const r = await fetch('/api/police-snapshots', {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ snapshot_date: date, source_kind: 'paste', rows }),
      });
      if (!r.ok) { setErr((await r.json()).error ?? 'Import failed'); return; }
      setPaste(''); onDone();
    } finally { setBusy(false); }
  }

  return (
    <div className={compact ? '' : 'card card-pad'}>
      {!compact && <>
        <h3 style={{ marginTop: 0 }}>Import the police device list</h3>
        <p className="muted" style={{ fontSize: 13 }}>
          Upload the Word document the police gave you, or paste the rows. The list is undated, so enter
          the date it reflects (“as held by police on”). The snapshot is frozen, hashed and anchored —
          so you can later prove what the police held, and when.
        </p>
      </>}
      <label className="fld" style={{ maxWidth: 240 }}><span>As held by police on</span>
        <input type="date" value={date} max={todayISO()} onChange={(e) => setDate(e.target.value)} />
      </label>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 10 }}>
        <label className="btn btn-primary" style={{ cursor: 'pointer' }}>
          {busy ? 'Importing…' : 'Upload .docx'}
          <input type="file" accept=".docx" style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) importDocx(f); }} />
        </label>
        <form onSubmit={importPaste} style={{ flex: 1, minWidth: 260 }}>
          <textarea value={paste} onChange={(e) => setPaste(e.target.value)} rows={3} placeholder="or paste one device per line: e.g.  samsung / s23"
            style={{ width: '100%', fontFamily: 'var(--mono)', fontSize: 12.5 }} />
          <button className="btn btn-ghost" type="submit" disabled={busy} style={{ marginTop: 6 }}>Import pasted list</button>
        </form>
      </div>
      {err && <div style={{ color: 'var(--bad)', fontSize: 12.5, marginTop: 8 }}>{err}</div>}
    </div>
  );
}
